// useEnrollmentCompleteness — backs the Home "complete your enrollment" CTA
// (Task 4, post-login routing for incomplete company coworkers). Calls
// `GET /api/enroll/me` (via `getMyEnrollment`) exactly once for role-'user'
// sessions and reports whether the completeness report's `missing` list is
// non-empty. Fails safe everywhere else per the change_spec: non-'user'
// roles (admin/operator), a 404 (no linked coworker), or any other error
// all resolve to `incomplete: false` — never nag when we can't be sure.
import { useEffect, useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { useHasRole } from '../auth/useRoles'
import { getMyEnrollment } from '../api/enrollMe'

export interface EnrollmentCompleteness {
  /** True only once a confirmed `missing`-non-empty report comes back for a
   * role-'user' session. Starts `false` and stays `false` on any error. */
  incomplete: boolean
  loading: boolean
}

export function useEnrollmentCompleteness(): EnrollmentCompleteness {
  const { getAccessTokenSilently } = useAuth0()
  const isUser = useHasRole('user')
  const [incomplete, setIncomplete] = useState(false)
  const [loading, setLoading] = useState(isUser)

  useEffect(() => {
    if (!isUser) {
      setIncomplete(false)
      setLoading(false)
      return
    }

    let cancelled = false
    setLoading(true)

    async function check() {
      try {
        const token = await getAccessTokenSilently()
        const report = await getMyEnrollment(token)
        if (!cancelled) setIncomplete(report.missing.length > 0)
      } catch {
        // Fail safe: 404 (no linked coworker), 403 (email not verified), or
        // any transient error — never nag when we can't confirm the state.
        if (!cancelled) setIncomplete(false)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    void check()
    return () => {
      cancelled = true
    }
  }, [isUser, getAccessTokenSilently])

  return { incomplete, loading }
}
